import { t } from 'ttag';

import {
    DocumentCategory,
    DocumentFirstSignBy,
    DocumentType,
} from '../../enums';
import { SignDocValidationResultType } from './enums';

export const SIGN_DOC_VALIDATION_TEXT: {
    [keys in SignDocValidationResultType]: string;
} = {
    [SignDocValidationResultType.OK]: 'Документ можна підписати',
    [SignDocValidationResultType.FINISHED]: 'Документи вже було підписано.',
    [SignDocValidationResultType.ON_RECIPIENT_SIDE]:
        'Документи вже було підписано від імені вашої компанії.',
    [SignDocValidationResultType.WAIT_REQUIRED_REVIEW]:
        'Документи знаходяться на погодженні у ваших співробітників. Коли документ буде погоджено, його можна буде підписати.',
    [SignDocValidationResultType.WAIT_SIGN_ORDER]:
        'Ще не настала ваша черга підписувати. Зачекайте, доки документ підпишуть ваші співробітники.',
    [SignDocValidationResultType.ONE_SIGN_DOCUMENT_ONLY_FOR_CONTR_AGENT]:
        'Документ не можна підписати, оскільки він не очікує підпису компанії.',
    [SignDocValidationResultType.NOT_DOCUMENT_SIGNER]:
        'Документи повинні підписати інші співробітники. Ви не у переліку підписантів.',
    [SignDocValidationResultType.MISSING_RECIPIENT]:
        'Перш ніж підписати, наступним документам необхідно вказати контрагентів:',
};
export const DOCUMENT_SIGN_ORDER_OPTIONS: Array<{
    value: string;
    label: string;
}> = [
    {
        label: t`Перший підпис від вашої компанії`,
        value: DocumentFirstSignBy.FIRST_SIGN_BY_OWNER,
    },
    {
        label: t`Перший підпис від контрагента`,
        value: DocumentFirstSignBy.FIRST_SIGN_BY_RECIPIENT,
    },
];

const ALLOWED_DOCUMENT_TYPES: DocumentType[] = [
    DocumentType.MULTILATERAL,
    DocumentType.BILATERAL,
    DocumentType.INTERNAL,
    DocumentType.BILATERAL_ONE_SIGN,
];

export const DOCUMENT_TYPES_OPTIONS: Array<{
    value: DocumentType;
    label: string;
}> = [
    {
        value: DocumentType.BILATERAL,
        label: t`Двосторонні документи`,
    },
    {
        value: DocumentType.BILATERAL_ONE_SIGN,
        label: t`Двосторонні документи, підпис однієї сторони`,
    },
    {
        value: DocumentType.MULTILATERAL,
        label: t`Багатосторонні документи`,
    },
    {
        value: DocumentType.INTERNAL,
        label: t`Внутрішні документи`,
    },
].filter((item) => ALLOWED_DOCUMENT_TYPES.includes(item.value));

export const DOCUMENT_CATEGORY_LOCALIZE_MAP = {
    [DocumentCategory.ANY]: t`Всі вхідні документи`,
    [DocumentCategory.ACT]: t`Акт наданих послуг`,
    [DocumentCategory.BILL]: t`Рахунок`,
    [DocumentCategory.CONTRACT]: t`Договір`,
    [DocumentCategory.ADDITIONAL_CONTRACT]: t`Додаткова угода до договору`,
    [DocumentCategory.SALES_INVOICE]: t`Видаткова накладна`,
    [DocumentCategory.TTN]: t`Товарно-транспортна накладна`,
    [DocumentCategory.OTHER]: t`Тип не обрано`,
    [DocumentCategory.EDI]: t`EDI документи`,
    [DocumentCategory.POWER_OF_ATTORNEY]: t`Довіреність`,
    [DocumentCategory.SPECIFICATION]: t`Специфікація`,
    [DocumentCategory.STATEMENT]: t`Заява`,
    [DocumentCategory.ACT_OF_ACCEPTANCE_TRANSFER]: t`Акт приймання-передачі`,
    [DocumentCategory.RETURNING]: t`Повернення`,
    [DocumentCategory.ORDER]: t`Замовлення`,
    [DocumentCategory.APPENDIX]: t`Додаток`,
    [DocumentCategory.ANOTHER]: t`Інший`,
    [DocumentCategory.VERIFICATION_ACT]: t`Акт звіряння`,
    [DocumentCategory.DIFFERENCES_PROTOCOL]: t`Протокол розбіжностей`,
    [DocumentCategory.REPORT]: t`Звіт`,
    [DocumentCategory.LETTER]: t`Лист`,
    [DocumentCategory.PROTOCOL]: t`Протокол`,
    [DocumentCategory.DECREE]: t`Наказ`,
    [DocumentCategory.ACT_OF_RETURN]: t`Акт повернення`,
    [DocumentCategory.ADJUSTMENT_CALCULATION]: t`Розрахунок коригування`,
    [DocumentCategory.ACT_OF_ADJUSTMENT]: t`Акт коригування`,
    [DocumentCategory.LETTER_OF_GUARANTEE]: t`Гарантійний лист`,
    [DocumentCategory.ACCOUNTING_MEMO]: t`Бухгалтерська довідка`,
    [DocumentCategory.WRITE_OFF_ACT]: t`Акт списання`,
    [DocumentCategory.ADJUSTMENT_INVOICE]: t`Коригуюча накладна`,
    [DocumentCategory.ADVANCE_REPORT]: t`Авансовий звіт`,
    [DocumentCategory.INVENTORY_ACT]: t`Акт інвентаризації`,
    [DocumentCategory.BUDGET]: t`Кошторис`,
    [DocumentCategory.TECH_SPECIFICATION]: t`Технічне завдання`,
    [DocumentCategory.FINANCIAL_CERTIFICATE]: t`Фінансова довідка`,
    [DocumentCategory.FINANCIAL_REPORTING]: t`Фінансова звітність`,
    [DocumentCategory.OFFER]: t`Оферта`,
    [DocumentCategory.RECEIVING_NOTE]: t`Прибуткова накладна`,
};
const DOCUMENT_CATEGORY_ALL_OPTIONS: Array<{
    value: string;
    label: string;
}> = [
    ...Object.keys(DOCUMENT_CATEGORY_LOCALIZE_MAP)
        // Move ANOTHER & EDI to the end of list
        .filter(
            (val) =>
                val !== DocumentCategory.ANOTHER &&
                val !== DocumentCategory.EDI,
        )
        .map((val) => ({
            value: val,
            label: DOCUMENT_CATEGORY_LOCALIZE_MAP[val as DocumentCategory.ANY],
        }))
        .sort((a, b) =>
            a.label.toLowerCase() <= b.label.toLowerCase() ? -1 : 1,
        ),
    {
        value: DocumentCategory.EDI,
        label: DOCUMENT_CATEGORY_LOCALIZE_MAP[DocumentCategory.EDI],
    },
    {
        value: DocumentCategory.ANOTHER,
        label: DOCUMENT_CATEGORY_LOCALIZE_MAP[DocumentCategory.ANOTHER],
    },
];

export const DOCUMENT_CATEGORY_OPTIONS = DOCUMENT_CATEGORY_ALL_OPTIONS.filter(
    (item) => item.value !== DocumentCategory.ANY,
);

export const DOCUMENT_CATEGORY_OPTIONS_FOR_INCOMING_DOCUMENTS = [
    // Remove OTHER, replace ANOTHER and move ANY to the end of list
    ...DOCUMENT_CATEGORY_ALL_OPTIONS.filter(
        (item) =>
            item.value !== DocumentCategory.OTHER &&
            item.value !== DocumentCategory.ANY &&
            item.value !== DocumentCategory.ANOTHER,
    ),
    {
        value: DocumentCategory.ANY,
        label: DOCUMENT_CATEGORY_LOCALIZE_MAP[DocumentCategory.ANY],
    },
    {
        value: DocumentCategory.ANOTHER,
        label: DOCUMENT_CATEGORY_LOCALIZE_MAP[DocumentCategory.ANOTHER],
    },
];

export const DOCUMENT_CATEGORY_OPTIONS_FOR_SIGN_POPUP = DOCUMENT_CATEGORY_ALL_OPTIONS.filter(
    (item) =>
        item.value !== DocumentCategory.OTHER &&
        item.value !== DocumentCategory.ANY,
);

export const DOCUMENT_CATEGORY_FOLDER: Array<{
    value: string;
    label: string;
}> = [
    {
        value: 'all',
        label: t`Всі`,
    },
    ...DOCUMENT_CATEGORY_OPTIONS,
];

export const DOCUMENT_VERSION_STATUS_INFO = {
    // versioned document statuses
    DOWNLOADED: {
        color: 'Gray',
        ownerTitle: t`Версія документа завантажена`,
        recipientTitle: '',
    },

    SENT: {
        color: 'Orange',
        ownerTitle: t`Версія документа відправлена`,
        recipientTitle: t`Версія документа отримана`,
    },
};

// Elastic search Limit 10 000 documens
export const ES_SEARCH_DOCUMENT_LIMIT: number = 10000;
